import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Send, CheckCircle2 } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_f59c783b-4061-4c90-8631-f537af31e122/artifacts/nm25t9qa_Calcutta%20Logo.png";

export default function GlobalAssistance() {
  const [form, setForm] = useState({ country: "", ip_type: "", requirement: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [ref, setRef] = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/public/global-assistance", form);
      setRef(data);
      toast.success("Request received");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3] text-slate-900" data-testid="assistance-page">
      <header className="border-b border-slate-200 bg-[#FBF8F3]/95 backdrop-blur sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO} alt="Calcutta" className="w-9 h-9 rounded-sm" />
            <div className="leading-none">
              <div className="font-serif text-lg font-semibold tracking-tight">Calcutta</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">by Mahakali Tribunal</div>
            </div>
          </Link>
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1">
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 md:px-8 py-20 md:py-24">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Global Assistance</div>
        <h1 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05] mt-4">
          Tell us what you need.
        </h1>
        <p className="mt-6 text-slate-700 text-base leading-relaxed max-w-xl">
          Calcutta operates India-first, but our platform is usable worldwide from Day 1. For jurisdictions or workflows we don't yet automate, tell us the requirement — the Mahakali Tribunal team will respond by email regarding available international support.
        </p>

        {ref ? (
          <div className="mt-12 border border-slate-300 bg-white p-8" data-testid="assistance-result">
            <CheckCircle2 size={28} className="text-[#B33A3A] mb-4" strokeWidth={1.5} />
            <h2 className="font-serif text-2xl font-semibold">Request received.</h2>
            <p className="text-sm text-slate-600 mt-3 leading-relaxed">{ref.message}</p>
            <div className="mt-6 text-xs text-slate-500">
              Reference: <span className="font-mono text-slate-800">CAL-REQ-{ref.reference}</span>
            </div>
            <button
              onClick={() => { setRef(null); setForm({ country:"", ip_type:"", requirement:"", email:"", message:"" }); }}
              className="mt-6 text-sm text-slate-900 hover:text-[#B33A3A] font-medium border-b border-slate-900 hover:border-[#B33A3A]"
              data-testid="another-btn"
            >
              Submit another request →
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-12 space-y-6 border border-slate-300 bg-white p-8" data-testid="assistance-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold block mb-2">Country</label>
                <input required value={form.country} onChange={(e) => upd("country", e.target.value)} data-testid="country-input"
                  placeholder="e.g. United Kingdom"
                  className="w-full border-b border-slate-300 py-2 text-sm focus:outline-none focus:border-slate-900 bg-transparent" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold block mb-2">IP Type (optional)</label>
                <select value={form.ip_type} onChange={(e) => upd("ip_type", e.target.value)} data-testid="ip-type-input"
                  className="w-full border-b border-slate-300 py-2 text-sm focus:outline-none focus:border-slate-900 bg-transparent">
                  <option value="">Any / not sure</option>
                  <option>Patent</option>
                  <option>Trademark</option>
                  <option>Copyright</option>
                  <option>Design</option>
                  <option>Software / Source code</option>
                  <option>Trade secret</option>
                  <option>Other</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold block mb-2">What do you need?</label>
              <input required value={form.requirement} onChange={(e) => upd("requirement", e.target.value)} data-testid="requirement-input"
                placeholder="e.g. Trademark filing in the UK"
                className="w-full border-b border-slate-300 py-2 text-sm focus:outline-none focus:border-slate-900 bg-transparent" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold block mb-2">Email</label>
              <input required type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} data-testid="email-input"
                placeholder="you@company.com"
                className="w-full border-b border-slate-300 py-2 text-sm focus:outline-none focus:border-slate-900 bg-transparent" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold block mb-2">Message (optional)</label>
              <textarea rows={4} value={form.message} onChange={(e) => upd("message", e.target.value)} data-testid="message-input"
                placeholder="Anything you'd like us to know."
                className="w-full border border-slate-300 p-3 text-sm focus:outline-none focus:border-slate-900" />
            </div>
            <button type="submit" disabled={loading} data-testid="submit-btn"
              className="bg-[#B33A3A] hover:bg-[#A02538] disabled:opacity-60 text-white px-5 py-2.5 text-sm font-medium rounded-sm flex items-center gap-2">
              <Send size={14} /> {loading ? "Sending…" : "Send request"}
            </button>
            <p className="text-xs text-slate-500 leading-relaxed">
              We typically respond within 2 business days. This form does not create an attorney-client relationship or constitute legal advice.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}
